import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl, injectIntl } from 'react-intl';

import type { CreateFlagArgs } from '@atlaskit/flag/flag-provider';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { useFlags } from '@atlaskit/flag/use-flags';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import { addUFOCustomData } from '@atlaskit/react-ufo/add-ufo-custom-data';
import { token } from '@atlaskit/tokens';

import {
	initializeGlobalState,
	sendPackageOperationalEvent,
	sendTrackEvent,
	sendUIEvent,
} from '../../../common/utils';
import { BSCIntlProvider } from '../../../common/utils/locale';
import { Logger } from '../../../common/utils/logger';
import {
	ACCEPT_ALL_PREFERENCES,
	ATLASSIAN_BANNER_LAST_DISMISSED_TIME_COOKIE_KEY,
	ONLY_NECESSARY_PREFERENCES,
} from '../../../constants';
import { deleteCookie, getCookie } from '../../../controllers/cookie-controls';
import { updatePreferences } from '../../../controllers/preferences-controls/update-preferences';
import type { ConsentDisplayedTextMap } from '../../../services/consent-hub-service/types';
import { i18n } from '../messages';
import { PreferenceModal } from '../preference-modal';
import usePreferences from '../preference-modal/use-preferences';
import { PresentationalConsentBanner } from '../presentational-consent-banner';
import type { ConsentBannerProps } from '../types';
import useBannerVisibility from '../use-banner-visibility';

const SuccessWithIntl = injectIntl(({ intl }) => (
	<SuccessIcon
		color={token('color.icon.success')}
		label={intl.formatMessage({
			id: 'browser-storage-controls.consent-banner.toast.success',
			defaultMessage: 'Success',
			description:
				'The text is shown as the title of a success toast notification that appears in the consent banner after the user successfully saves their cookie preferences.',
		})}
		spacing="spacious"
	/>
));

const ErrorWithIntl = injectIntl(({ intl }) => (
	<ErrorIcon
		color={token('color.icon.danger')}
		label={intl.formatMessage({
			id: 'browser-storage-controls.consent-banner.toast.error',
			defaultMessage: 'Error',
			description: 'The title for an error as a single word.',
		})}
		spacing="spacious"
	/>
));

const errorToastProps = (): Omit<CreateFlagArgs, 'title'> => ({
	icon: <ErrorWithIntl />,
	appearance: 'normal' as const,
});

const successToastProps = (): Omit<CreateFlagArgs, 'title'> => ({
	icon: <SuccessWithIntl />,
	appearance: 'normal' as const,
	isAutoDismiss: true,
});

export const ConsentBannerImpl = ({
	onShow,
	onHide,
	initialPreferences,
	minWidth = 400,
	product,
	onConsentBannerBailOut,
	analyticsEnabled = true,
	localConsentMode = false,
	newMinHeight,
	ssrOptions,
	perimeter,
}: ConsentBannerProps): React.JSX.Element | null => {
	const firstRender = useRef(true);
	useEffect(() => {
		if (!firstRender.current) {
			return;
		}

		// Send an operational event if the banner is shown after previously having
		// been dismissed. This will help prevent issues with the banner
		// inappropriately reappearing.
		const lastBannerDismissal = getCookie(ATLASSIAN_BANNER_LAST_DISMISSED_TIME_COOKIE_KEY);
		if (lastBannerDismissal) {
			const timeSinceDismissal =
				Date.now() - new Date(Number.parseInt(lastBannerDismissal)).getTime();

			const lessThanOneHour = timeSinceDismissal < 1000 * 60 * 60;
			const lessThanOneDay = timeSinceDismissal < 1000 * 60 * 60 * 24;
			const lessThanOneWeek = timeSinceDismissal < 1000 * 60 * 60 * 24 * 7;

			sendPackageOperationalEvent({
				action: 'bannerAppearanceAfterDismissal',
				attributes: { timeSinceDismissal, lessThanOneHour, lessThanOneDay, lessThanOneWeek },
			});

			deleteCookie(ATLASSIAN_BANNER_LAST_DISMISSED_TIME_COOKIE_KEY);
		}

		firstRender.current = false;
	}, []);

	useEffect(() => {
		initializeGlobalState({ analyticsEnabled, product, localConsentMode, perimeter });
	}, [analyticsEnabled, product, localConsentMode, perimeter]);

	const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);

	const intl = useIntl();

	const { showFlag } = useFlags();

	const { preferences, isPrefetchedPreferencesState, isInitialRender } = usePreferences({
		initialPreferences,
	});

	const { showBanner, setIsDismissed } = useBannerVisibility({
		preferences,
		isPrefetchedPreferencesState,
		initialPreferences,
		isInitialRender,
		ssrOptions,
	});

	const closePreferenceModal = useCallback(() => {
		setIsPreferencesModalOpen(false);
	}, [setIsPreferencesModalOpen]);

	const onPreferenceModalSubmit = useCallback(() => {
		closePreferenceModal();
		setIsDismissed(true);
	}, [closePreferenceModal, setIsDismissed]);

	const onClickPreferences = useCallback(() => {
		setIsPreferencesModalOpen(true);

		sendUIEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'preferences',
			source: 'cookieConsentBanner',
		});
	}, []);

	const showSuccessFlag = useCallback(() => {
		if (localConsentMode) {
			showFlag({
				...successToastProps(),
				id: 'cookieConsentBanner',
				description: intl.formatMessage(i18n.successFlagDescriptionLocalOnly),
				title: intl.formatMessage(i18n.successFlagTitle),
			});
		} else {
			showFlag({
				...successToastProps(),
				id: 'cookieConsentBanner',
				description: intl.formatMessage(i18n.successFlagDescription),
				title: intl.formatMessage(i18n.successFlagTitle),
			});
		}
	}, [intl, localConsentMode, showFlag]);

	const showErrorFlag = useCallback(() => {
		showFlag({
			...errorToastProps(),
			id: 'cookieConsentBanner',
			description: intl.formatMessage(i18n.errorFlagDescription),
			title: intl.formatMessage(i18n.errorFlagTitle),
		});
	}, [intl, showFlag]);

	// Sets preferences to allow only Strictly Necessary cookies
	const onClickOnlyNecessary = useCallback(
		async (displayedTextMap: ConsentDisplayedTextMap) => {
			try {
				sendUIEvent({
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'onlyNecessary',
					source: 'cookieConsentBanner',
				});

				await updatePreferences(ONLY_NECESSARY_PREFERENCES, displayedTextMap);

				sendTrackEvent({
					source: 'cookieConsentBanner',
					action: 'submitted',
					actionSubject: 'cookieConsentPreferences',
				});

				showSuccessFlag();
			} catch (e: any) {
				Logger.errorWithOperationalEvent({
					action: 'updatePreferencesError',
					attributes: {
						preferences: ONLY_NECESSARY_PREFERENCES,
						product,
					},
					message: `Failed to update preferences from banner. ${e.message || ''}`,
				});

				showErrorFlag();
			} finally {
				setIsDismissed(true);
			}
		},
		[product, showErrorFlag, showSuccessFlag, setIsDismissed],
	);

	// Sets preferences to allow all categories of cookies
	const onClickAcceptAll = useCallback(
		async (displayedTextMap: ConsentDisplayedTextMap) => {
			try {
				sendUIEvent({
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'acceptAll',
					source: 'cookieConsentBanner',
				});

				await updatePreferences(ACCEPT_ALL_PREFERENCES, displayedTextMap);

				sendTrackEvent({
					source: 'cookieConsentBanner',
					action: 'submitted',
					actionSubject: 'cookieConsentPreferences',
				});

				showSuccessFlag();
			} catch (e: any) {
				Logger.errorWithOperationalEvent({
					action: 'updatePreferencesError',
					attributes: {
						preferences: ACCEPT_ALL_PREFERENCES,
						product,
					},
					message: `Failed to update preferences from banner. ${e.message || ''}`,
				});

				showErrorFlag();
			} finally {
				setIsDismissed(true);
			}
		},
		[product, showErrorFlag, showSuccessFlag, setIsDismissed],
	);

	const onClickTrackingNoticeLink = useCallback(() => {
		sendUIEvent({
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'cookiesTrackingNotice',
			source: 'cookieConsentBanner',
		});
	}, []);

	// Call onShow/onHide as a side effect of showBanner changing, rather than
	// possibly having to call them in multiple places.
	useEffect(() => {
		if (showBanner) {
			addUFOCustomData({
				isCookieConsentBannerDisplayed: true,
			});
		}
		if (showBanner) {
			onShow?.();
		} else if (!showBanner) {
			onHide?.();
		}
	}, [showBanner, onShow, onHide]);

	useEffect(() => {
		// when banner is not visible and isPrefetchedPreferencesState is in "resolved" state
		if (!showBanner && isPrefetchedPreferencesState !== undefined) {
			// report "banner decided not to show" to sync decision with other components
			onConsentBannerBailOut?.();
		}
	}, [showBanner, isPrefetchedPreferencesState, onConsentBannerBailOut]);

	if (!showBanner) {
		return null;
	}

	return (
		<FlagsProvider>
			<BSCIntlProvider>
				<PresentationalConsentBanner
					onClickOnlyNecessary={onClickOnlyNecessary}
					onClickAcceptAll={onClickAcceptAll}
					onClickPreferences={onClickPreferences}
					onClickTrackingNoticeLink={onClickTrackingNoticeLink}
					minWidth={minWidth}
					newMinHeight={newMinHeight}
				/>
				{isPreferencesModalOpen && (
					<PreferenceModal
						onClose={closePreferenceModal}
						onSubmit={onPreferenceModalSubmit}
						showSuccessFlag={showSuccessFlag}
						showErrorFlag={showErrorFlag}
						shouldAddWrappers={false}
					/>
				)}
			</BSCIntlProvider>
		</FlagsProvider>
	);
};
